import { useMemo } from "react";
import { Card } from "@/types/v2";
import { isPast } from "date-fns";
import { CardGroup, GroupStatistics, GroupingConfig } from "../types";
import { firestoreToDateTime } from "@/lib/utils";

export interface UseListViewGroupingProps {
  cards: Card[];
  config: GroupingConfig;
  employees?: Array<{
    id: string | number;
    firstName: string;
    lastName: string;
  }>;
  containers?: Array<{ id: string; name: string }>;
  sortCards?: (cards: Card[]) => Card[];
}

export interface UseListViewGroupingReturn {
  groups: CardGroup[];
  totalCards: number;
  totalGroups: number;
  ungroupedCards: Card[];
}

export const useListViewGrouping = ({
  cards,
  config,
  employees = [],
  containers = [],
  sortCards,
}: UseListViewGroupingProps): UseListViewGroupingReturn => {
  const { groupBy, collapsedGroups } = config;

  // Create lookup maps for performance optimization
  const employeeLookup = useMemo(() => {
    return new Map(employees.map((emp) => [`${emp.id}`, emp]));
  }, [employees]);

  const containerLookup = useMemo(() => {
    return new Map(containers.map((cont) => [cont.id, cont]));
  }, [containers]);

  const groups = useMemo(() => {
    if (groupBy === "none") {
      return [];
    }

    const startTime = performance.now();

    // Group cards by the specified field
    const cardGroups = new Map<string, Card[]>();

    cards.forEach((card) => {
      let groupKey: string;

      switch (groupBy) {
        case "container":
          groupKey = card.currentContainer || "ungrouped";
          break;

        case "technician":
          groupKey =
            card.technicianId && card.technicianId !== "none"
              ? `${card.technicianId}`
              : "ungrouped";
          break;

        case "serviceAdvisor":
          groupKey =
            card.serviceWriterId && card.serviceWriterId !== "none"
              ? `${card.serviceWriterId}`
              : "ungrouped";
          break;

        default:
          groupKey = "ungrouped";
      }

      if (!cardGroups.has(groupKey)) {
        cardGroups.set(groupKey, []);
      }
      cardGroups.get(groupKey)!.push(card);
    });

    // Convert to CardGroup array with statistics
    const result = Array.from(cardGroups.entries())
      .map(([groupKey, groupCards]): CardGroup => {
        const statistics = calculateGroupStatistics(groupCards);
        const isCollapsed = collapsedGroups.includes(groupKey);

        // For display, use a more user-friendly label
        let displayLabel: string;
        switch (groupBy) {
          case "container":
            const containerForLabel = containerLookup.get(groupKey);
            displayLabel = containerForLabel?.name || groupKey;
            break;
          case "technician":
            const technicianForLabel = employeeLookup.get(groupKey);
            displayLabel = technicianForLabel
              ? `${technicianForLabel.firstName} ${technicianForLabel.lastName}`.trim()
              : groupKey === "ungrouped"
                ? "Unassigned or Unknown Technician"
                : groupKey;
            break;
          case "serviceAdvisor":
            const serviceAdvisorForLabel = employeeLookup.get(groupKey);
            displayLabel = serviceAdvisorForLabel
              ? `${serviceAdvisorForLabel.firstName} ${serviceAdvisorForLabel.lastName}`.trim()
              : groupKey === "ungrouped"
                ? "Unassigned or Unknown Service Advisor"
                : groupKey;
            break;
          default:
            displayLabel = groupKey;
        }

        // Apply sorting to cards within each group
        const sortedGroupCards = sortCards ? sortCards(groupCards) : groupCards;

        return {
          id: groupKey,
          label: `${displayLabel}`,
          cards: sortedGroupCards,
          statistics,
          isCollapsed,
        };
      })
      .sort((a, b) => {
        // Sort groups: ungrouped last, then alphabetically
        if (a.id === "ungrouped") return 1;
        if (b.id === "ungrouped") return -1;
        return a.label.localeCompare(b.label);
      });

    // Performance monitoring
    const endTime = performance.now();
    const groupingTime = endTime - startTime;

    if (groupingTime > 200) {
      console.warn(
        `Dashboard List Grouping: Slow grouping operation (${groupingTime.toFixed(2)}ms) with ${cards.length} cards in ${cardGroups.size} groups`
      );
    } else if (cards.length > 1000) {
      console.info(
        `Dashboard List Grouping: Large dataset processed (${cards.length} cards) in ${groupingTime.toFixed(2)}ms`
      );
    }

    return result;
  }, [
    cards,
    groupBy,
    collapsedGroups,
    employeeLookup,
    containerLookup,
    sortCards,
  ]);

  const ungroupedCards = useMemo(() => {
    if (groupBy !== "none") {
      return [];
    }
    // Apply sorting to ungrouped cards as well
    return sortCards ? sortCards(cards) : cards;
  }, [cards, groupBy, sortCards]);

  const totalCards = cards.length;
  const totalGroups = groups.length;

  return {
    groups,
    totalCards,
    totalGroups,
    ungroupedCards,
  };
};

/**
 * Calculate statistics for a group of cards
 */
function calculateGroupStatistics(cards: Card[]): GroupStatistics {
  const cardCount = cards.length;

  // Calculate total labor hours
  const totalLaborHours = cards.reduce((total, card) => {
    const laborHours = card.totalLaborHours?.estimatedLabor || 0;
    return total + laborHours;
  }, 0);

  // Calculate average labor hours
  const averageLaborHours = cardCount > 0 ? totalLaborHours / cardCount : 0;

  // Count promise time violations (cards past their promised time)
  const promiseTimeViolations = cards.filter((card) => {
    if (!card.promisedTime) return false;
    try {
      return isPast(firestoreToDateTime(card.promisedTime) ?? new Date());
    } catch {
      return false;
    }
  }).length;

  return {
    cardCount,
    totalLaborHours,
    averageLaborHours,
    promiseTimeViolations,
  };
}
